/**
 * 统一的 HTTP 客户端
 * 提供统一的请求处理、错误处理、认证和拦截器功能
 */

export interface HttpClientConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export interface RequestConfig {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  timeout?: number;
  skipAuthErrorHandling?: boolean;
}

export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  status: number;
  success: boolean;
  skipAuthErrorHandling?: boolean;
  [key: string]: unknown;
}

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class HttpClient {
  private config: HttpClientConfig;
  private requestInterceptors: Array<
    (config: RequestConfig) => RequestConfig | Promise<RequestConfig>
  > = [];
  private responseInterceptors: Array<
    (response: ApiResponse) => ApiResponse | Promise<ApiResponse>
  > = [];

  private ownsKey(value: object, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(value, key);
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private toBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private toNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' ? value : fallback;
  }

  private toMessage(value: unknown, fallback: string): string {
    return typeof value === 'string' && value ? value : fallback;
  }

  private toExtraFields(value: unknown): Record<string, unknown> | undefined {
    return this.isRecord(value) ? value : undefined;
  }

  constructor(config: HttpClientConfig = {}) {
    this.config = {
      baseURL: '',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      ...config,
    };
  }

  /**
   * 添加请求拦截器
   */
  addRequestInterceptor(
    interceptor: (config: RequestConfig) => RequestConfig | Promise<RequestConfig>
  ) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * 添加响应拦截器
   */
  addResponseInterceptor(
    interceptor: (response: ApiResponse) => ApiResponse | Promise<ApiResponse>
  ) {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * 执行请求拦截器
   */
  private async executeRequestInterceptors(config: RequestConfig): Promise<RequestConfig> {
    let processedConfig = config;
    for (const interceptor of this.requestInterceptors) {
      processedConfig = await interceptor(processedConfig);
    }
    return processedConfig;
  }

  /**
   * 执行响应拦截器
   */
  private async executeResponseInterceptors(response: ApiResponse): Promise<ApiResponse> {
    let processedResponse = response;
    for (const interceptor of this.responseInterceptors) {
      processedResponse = await interceptor(processedResponse);
    }
    return processedResponse;
  }

  /**
   * 构建查询参数
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    return searchParams.toString();
  }

  /**
   * 将蛇形命名转换为驼峰命名
   */
  private toCamelCase(str: string): string {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  /**
   * 将驼峰命名转换为蛇形命名
   */
  private toSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  }

  /**
   * 递归转换对象的键名从蛇形到驼峰（后端响应 → 前端）
   */
  private convertKeysToCamelCase(obj: unknown): unknown {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.convertKeysToCamelCase(item));
    }

    if (this.isRecord(obj)) {
      const converted: Record<string, unknown> = {};
      for (const key in obj) {
        if (this.ownsKey(obj, key)) {
          const camelKey = this.toCamelCase(key);
          converted[camelKey] = this.convertKeysToCamelCase(obj[key]);
        }
      }
      return converted;
    }

    return obj;
  }

  /**
   * 递归转换对象的键名从驼峰到蛇形（前端请求 → 后端）
   */
  private convertKeysToSnakeCase(obj: unknown): unknown {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.convertKeysToSnakeCase(item));
    }

    if (this.isRecord(obj)) {
      const converted: Record<string, unknown> = {};
      for (const key in obj) {
        if (this.ownsKey(obj, key)) {
          const snakeKey = this.toSnakeCase(key);
          converted[snakeKey] = this.convertKeysToSnakeCase(obj[key]);
        }
      }
      return converted;
    }

    return obj;
  }

  /**
   * 统一处理API响应数据结构
   * 智能解析嵌套的数据结构，提取实际数据
   */
  private processApiResponse(rawResponse: unknown): {
    success: boolean;
    data: unknown;
    status: number;
    message: string;
    extraFields?: Record<string, unknown>;
  } {
    // 如果响应本身就是数组，直接返回并转换字段命名
    if (Array.isArray(rawResponse)) {
      return {
        success: true,
        data: this.convertKeysToCamelCase(rawResponse),
        status: 200,
        message: 'OK',
      };
    }

    // 如果响应是对象
    if (this.isRecord(rawResponse)) {
      // 检查是否是标准的API响应格式
      if (this.ownsKey(rawResponse, 'success') && this.ownsKey(rawResponse, 'data')) {
        const { success, data, message, ...extraFields } = rawResponse;
        const convertedExtraFields = this.convertKeysToCamelCase(extraFields);
        // 情况1: 标准格式 { success: true, data: [...] }
        return {
          success: this.toBoolean(success, true),
          data: this.convertKeysToCamelCase(data),
          extraFields: this.toExtraFields(convertedExtraFields),
          status: 200,
          message: this.toMessage(message, 'OK'),
        };
      }

      // 检查是否是嵌套的响应格式 (httpClient包装的)
      if (this.isRecord(rawResponse.data)) {
        // 情况2: 嵌套格式 { data: { success: true, data: [...] }, status: 200 }
        if (this.ownsKey(rawResponse.data, 'success') && this.ownsKey(rawResponse.data, 'data')) {
          const { success, data, message, ...extraFields } = rawResponse.data;
          const convertedExtraFields = this.convertKeysToCamelCase(extraFields);
          return {
            success: this.toBoolean(success, true),
            data: this.convertKeysToCamelCase(data),
            extraFields: this.toExtraFields(convertedExtraFields),
            status: this.toNumber(rawResponse.status, 200),
            message: this.toMessage(rawResponse.message, this.toMessage(message, 'OK')),
          };
        }

        // 情况3: 简单嵌套 { data: [...], status: 200 }
        return {
          success: rawResponse.success !== false, // 默认为true，除非明确为false
          data: this.convertKeysToCamelCase(rawResponse.data),
          status: this.toNumber(rawResponse.status, 200),
          message: this.toMessage(rawResponse.message, 'OK'),
        };
      }

      // 情况4: 直接的对象响应，可能包含业务数据
      return {
        success: rawResponse.success !== false,
        data: this.convertKeysToCamelCase(rawResponse),
        status: this.toNumber(rawResponse.status, 200),
        message: this.toMessage(rawResponse.message, 'OK'),
      };
    }

    // 其他情况：包装为标准格式
    return {
      success: true,
      data: rawResponse,
      status: 200,
      message: 'OK',
    };
  }

  /**
   * 核心请求方法
   */
  async request<T = unknown>(config: RequestConfig): Promise<ApiResponse<T>> {
    try {
      // 执行请求拦截器
      const processedConfig = await this.executeRequestInterceptors(config);

      // 构建 URL
      let url: URL;
      if (this.config.baseURL) {
        url = new URL(processedConfig.url, this.config.baseURL);
      } else {
        // 当baseURL为空时，使用当前域名
        url = new URL(processedConfig.url, window.location.origin);
      }

      if (processedConfig.params) {
        url.search = this.buildQueryString(processedConfig.params);
      }

      // 构建请求选项
      const fetchOptions: RequestInit = {
        method: processedConfig.method || 'GET',
        headers: {
          ...this.config.headers,
          ...processedConfig.headers,
        },
        credentials: 'include',
      };

      // 添加请求体
      if (processedConfig.data) {
        if (processedConfig.data instanceof FormData) {
          fetchOptions.body = processedConfig.data;
          // FormData 会自动设置 Content-Type
          delete (fetchOptions.headers as Record<string, string>)['Content-Type'];
        } else {
          // 将前端驼峰命名转换为后端蛇形命名
          const convertedData = this.convertKeysToSnakeCase(processedConfig.data);
          fetchOptions.body = JSON.stringify(convertedData);
        }
      }

      // 设置超时
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        processedConfig.timeout || this.config.timeout
      );
      fetchOptions.signal = controller.signal;

      // 发送请求
      const response = await fetch(url.toString(), fetchOptions);
      clearTimeout(timeoutId);

      // 解析响应
      let responseData: unknown;
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      // 构建响应对象
      const processedResponse = this.processApiResponse(responseData);

      const apiResponse: ApiResponse<T> = {
        data: processedResponse.data as T,
        status: response.status,
        success: processedResponse.success,
        message: processedResponse.message || response.statusText,
        skipAuthErrorHandling: processedConfig.skipAuthErrorHandling,
        ...(processedResponse.extraFields || {}),
      };

      // 先执行响应拦截器，让认证过期等全局逻辑有机会在抛错前清理状态
      const interceptedResponse = (await this.executeResponseInterceptors(
        apiResponse
      )) as ApiResponse<T>;

      if (!response.ok) {
        const errorPayload = this.isRecord(responseData) ? responseData : undefined;
        throw new HttpError(
          this.toMessage(
            errorPayload?.message,
            this.toMessage(errorPayload?.error, response.statusText || 'Request failed')
          ),
          response.status,
          responseData
        );
      }

      return interceptedResponse;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new HttpError('Request timeout', 408);
      }

      throw new HttpError(error instanceof Error ? error.message : 'Unknown error', 0);
    }
  }

  /**
   * GET 请求
   */
  async get<T = unknown>(
    url: string,
    params?: Record<string, unknown>,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      url,
      method: 'GET',
      params,
      ...config,
    });
  }

  /**
   * POST 请求
   */
  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      url,
      method: 'POST',
      data,
      ...config,
    });
  }

  /**
   * PUT 请求
   */
  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      url,
      method: 'PUT',
      data,
      ...config,
    });
  }

  /**
   * DELETE 请求
   */
  async delete<T = unknown>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({
      url,
      method: 'DELETE',
      ...config,
    });
  }

  /**
   * PATCH 请求
   */
  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      url,
      method: 'PATCH',
      data,
      ...config,
    });
  }
}

// 创建默认的 HTTP 客户端实例
export const httpClient = new HttpClient();
